/**
 * LiveTvOverlay — custom overlay for Live TV playback.
 *
 * v1.3.95 — Matches the Live TV mockup: no scrubber (live stream), a
 * rounded-border panel with the channel logo + `[group] name` title +
 * a "current programme  NEXT: next programme" EPG line, floating
 * UP / DOWN channel-zap chevrons on the right edge (outside the panel),
 * and a bottom-right icon row (aspect / CC / settings / audio).
 *
 * The disk icon from the mockup is intentionally omitted.
 *
 * Auto-hides after 3 s of no interaction. Wakes on mouse-move, touch,
 * or any keydown (D-pad remote friendly).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronUp,
  ChevronDown,
  LayoutGrid,
  Captions,
  SlidersHorizontal,
  Music,
} from "lucide-react";

// Aspect-ratio cycle for Live TV (same as the VOD overlay).
const ASPECT_MODES = ["contain", "cover", "fill"];
const ASPECT_LABELS = { contain: "Fit", cover: "Zoom", fill: "Stretch" };

// Formats an EPG programme window into "Title  H:MM am - H:MM am".
function formatEpgLine(prog) {
  if (!prog?.title) return "";
  const fmt = (d) => {
    if (!d) return "";
    try {
      const dt = d instanceof Date ? d : new Date(d);
      if (Number.isNaN(dt.getTime())) return "";
      return dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase();
    } catch { return ""; }
  };
  const start = fmt(prog.start);
  const stop = fmt(prog.stop);
  const window = start && stop ? ` ${start} - ${stop}` : "";
  return `${prog.title}${window}`;
}

export default function LiveTvOverlay({
  videoRef,
  channelName,
  channelGroup,
  channelLogo,
  currentProgramme,   // { title, start, stop } | null
  nextProgramme,      // { title, start, stop } | null
  canZapUp,
  canZapDown,
  onZapUp,
  onZapDown,
  onOpenSubtitles,
  onOpenAudioTracks,
  onOpenSettings,
}) {
  const [visible, setVisible] = useState(true);
  const [aspect, setAspect] = useState("contain");
  const hideTimer = useRef(null);

  const scheduleHide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setVisible(false), 3000);
  }, []);

  const wake = useCallback(() => {
    setVisible(true);
    scheduleHide();
  }, [scheduleHide]);

  useEffect(() => {
    const onMove = () => wake();
    const onKey = (e) => {
      // Also wire ArrowUp / ArrowDown → channel zap so D-pad remotes work.
      wake();
      if (e.key === "ArrowUp" && canZapUp && onZapUp) { e.preventDefault(); onZapUp(); }
      if (e.key === "ArrowDown" && canZapDown && onZapDown) { e.preventDefault(); onZapDown(); }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchstart", onMove, { passive: true });
    window.addEventListener("keydown", onKey);
    scheduleHide();
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchstart", onMove);
      window.removeEventListener("keydown", onKey);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [wake, scheduleHide, canZapUp, canZapDown, onZapUp, onZapDown]);

  useEffect(() => {
    const v = videoRef?.current;
    if (v && v.style) v.style.objectFit = aspect;
  }, [aspect, videoRef]);

  const cycleAspect = () => {
    setAspect((a) => ASPECT_MODES[(ASPECT_MODES.indexOf(a) + 1) % ASPECT_MODES.length]);
    wake();
  };

  const currentLine = formatEpgLine(currentProgramme);
  const nextLine = formatEpgLine(nextProgramme);
  const groupPrefix = channelGroup ? `[${channelGroup}] ` : "";

  return (
    <div
      data-testid="livetv-overlay"
      className={`absolute inset-0 z-30 transition-opacity duration-300 pointer-events-none ${
        visible ? "opacity-100" : "opacity-0"
      }`}
      onMouseEnter={wake}
    >
      {/* Right-edge channel-zap chevrons — outside the info panel, big
          blue tap targets. Only rendered when zapping is available. */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col gap-16 pointer-events-auto">
        <button
          type="button"
          onClick={onZapUp}
          disabled={!canZapUp}
          data-testid="livetv-zap-up"
          aria-label="Previous channel"
          className={`transition-colors ${
            canZapUp ? "text-sky-400 hover:text-sky-300" : "text-slate-700 cursor-not-allowed"
          }`}
        >
          <ChevronUp className="w-16 h-16" strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={onZapDown}
          disabled={!canZapDown}
          data-testid="livetv-zap-down"
          aria-label="Next channel"
          className={`transition-colors ${
            canZapDown ? "text-sky-400 hover:text-sky-300" : "text-slate-700 cursor-not-allowed"
          }`}
        >
          <ChevronDown className="w-16 h-16" strokeWidth={2} />
        </button>
      </div>

      {/* Bottom info panel — rounded border, semi-transparent black,
          contains channel logo + name + EPG lines. */}
      <div
        className="absolute inset-x-6 bottom-6 rounded-2xl border border-slate-600/70 bg-black/60 backdrop-blur-sm pointer-events-auto"
        onClick={wake}
      >
        <div className="flex items-center gap-5 px-6 py-4">
          {channelLogo && (
            <img
              src={channelLogo}
              alt=""
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-md object-contain flex-shrink-0 bg-black/40 p-1"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="livetv-overlay-logo"
            />
          )}
          <div className="min-w-0 flex-1">
            <div
              data-testid="livetv-overlay-title"
              className="text-white text-xl sm:text-2xl font-semibold truncate"
            >
              <span className="text-slate-300 font-normal">{groupPrefix}</span>
              {channelName}
            </div>
            {(currentLine || nextLine) && (
              <div
                data-testid="livetv-overlay-epg"
                className="text-sm mt-1 truncate"
              >
                {currentLine && (
                  <span className="text-emerald-400 font-medium">{currentLine}</span>
                )}
                {nextLine && (
                  <>
                    <span className="text-slate-400 mx-2">NEXT:</span>
                    <span className="text-amber-300">{nextLine}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom-right icon row inside the panel. */}
        <div className="border-t border-slate-700/60 px-6 py-3 flex justify-end items-center gap-6 text-white">
          <button
            type="button"
            onClick={cycleAspect}
            data-testid="livetv-btn-aspect"
            aria-label={`Aspect ratio: ${ASPECT_LABELS[aspect]}`}
            title={`Aspect: ${ASPECT_LABELS[aspect]} (click to cycle)`}
            className="p-1.5 hover:text-sky-400 transition-colors"
          >
            <LayoutGrid className="w-7 h-7" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onOpenSubtitles}
            data-testid="livetv-btn-subtitles"
            aria-label="Subtitles"
            className="p-1.5 hover:text-sky-400 transition-colors"
          >
            <Captions className="w-7 h-7" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onOpenSettings}
            data-testid="livetv-btn-settings"
            aria-label="Playback settings"
            className="p-1.5 hover:text-sky-400 transition-colors"
          >
            <SlidersHorizontal className="w-7 h-7" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onOpenAudioTracks}
            data-testid="livetv-btn-audio"
            aria-label="Audio tracks"
            className="p-1.5 hover:text-sky-400 transition-colors"
          >
            <Music className="w-7 h-7" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
